import React, { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";

export interface EventHistory {
    uuehId: number;
    uuehRefId: string;
    uuehEvnhId: number;
    uuehEvpaId: number;
    uuehTrnsId: number;
    uuehTrnsStatus: number;
    uuehTrnsStatusLabel: string;
    uuehCreatedTime: string;
    uuehUpdatedTime: string;
}

export interface EventHistoryResult {
    data: EventHistory[];
}

interface EventHistoryModalProps {
    isOpen: boolean;
    onClose: () => void;
    eventHistory?: EventHistoryResult;
    isLoading: boolean;
    error: Error | null;
}

const TableSkeleton: React.FC = () => (
    <tr className="animate-pulse">
        <td className="px-4 py-4">
            <div className="h-4 bg-gray-200 rounded w-32"></div>
        </td>
        <td className="px-4 py-4">
            <div className="h-6 bg-gray-200 rounded w-20"></div>
        </td>
        <td className="px-4 py-4">
            <div className="h-4 bg-gray-200 rounded w-24"></div>
        </td>
    </tr>
);

const StatusBadge: React.FC<{ status: string }> = ({ status }) => {
    const isPaid = status.toLowerCase() === "paid" || status.toLowerCase() === "lunas";
    const isExpired = status.toLowerCase() === "expired" || status.toLowerCase() === "kadaluarsa";

    return (
        <span
            className={`
                inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold
                ${isPaid
                    ? "bg-green-100 text-green-700 border border-green-200"
                    : isExpired
                        ? "bg-red-100 text-red-700 border border-red-200"
                        : "bg-yellow-100 text-yellow-700 border border-yellow-200"
                }
            `}
        >
            <span
                className={`
                    w-2 h-2 rounded-full mr-2
                    ${isPaid ? "bg-green-500" : isExpired ? "bg-red-500" : "bg-yellow-500"}
                `}
            />
            {status}
        </span>
    );
};

const formatDate = (dateString: string): string => {
    if (!dateString) return "-";
    const date = new Date(dateString);
    return date.toLocaleDateString("id-ID", {
        day: "numeric",
        month: "long",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
    });
};

const EventHistoryModal: React.FC<EventHistoryModalProps> = ({
    isOpen,
    onClose,
    eventHistory,
    isLoading,
    error,
}) => {
    const { t } = useTranslation();
    const modalRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (modalRef.current && !modalRef.current.contains(event.target as Node)) {
                onClose();
            }
        };

        if (isOpen) {
            document.addEventListener("mousedown", handleClickOutside);
            document.body.style.overflow = "hidden";
        }

        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
            document.body.style.overflow = "unset";
        };
    }, [isOpen, onClose]);

    useEffect(() => {
        const handleEscape = (event: KeyboardEvent) => {
            if (event.key === "Escape") {
                onClose();
            }
        };

        if (isOpen) {
            document.addEventListener("keydown", handleEscape);
        }

        return () => {
            document.removeEventListener("keydown", handleEscape);
        };
    }, [isOpen, onClose]);

    if (!isOpen) return null;

    const historyData = eventHistory?.data || [];
    const hasData = historyData.length > 0;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            {/* Backdrop */}
            <div
                className="absolute inset-0 bg-black/50 backdrop-blur-sm transition-opacity"
                aria-hidden="true"
            />

            {/* Modal Container */}
            <div
                ref={modalRef}
                className="relative bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden transform transition-all"
                role="dialog"
                aria-modal="true"
                aria-labelledby="modal-title"
            >
                {/* Header */}
                <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 bg-gradient-to-r from-brand-blue to-brand-blue/80">
                    <h2
                        id="modal-title"
                        className="text-xl font-bold text-white font-evil tracking-wide flex items-center gap-3"
                    >
                        <svg
                            className="w-6 h-6 text-brand-orange"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                        >
                            <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                            />
                        </svg>
                        {t("profile.eventHistory.title", "My Event History")}
                    </h2>
                    <button
                        onClick={onClose}
                        className="p-2 rounded-full text-white/80 hover:text-white hover:bg-white/10 transition-colors"
                        aria-label={t("common.close", "Close")}
                    >
                        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M6 18L18 6M6 6l12 12"
                            />
                        </svg>
                    </button>
                </div>

                {/* Content */}
                <div className="p-6 overflow-y-auto max-h-[calc(90vh-80px)]">
                    {isLoading ? (
                        <div className="space-y-4">
                            <div className="overflow-hidden rounded-xl border border-gray-200">
                                <table className="w-full">
                                    <thead className="bg-gray-50">
                                        <tr>
                                            <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                                                {t("profile.eventHistory.fields.refId", "Registration Code")}
                                            </th>
                                            <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                                                {t("profile.eventHistory.fields.status", "Status")}
                                            </th>
                                            <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                                                {t("profile.eventHistory.fields.date", "Registration Date")}
                                            </th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-gray-100">
                                        {[...Array(5)].map((_, i) => (
                                            <TableSkeleton key={i} />
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    ) : error ? (
                        <div className="text-center py-12">
                            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                                <svg className="w-8 h-8 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth={2}
                                        d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                                    />
                                </svg>
                            </div>
                            <h3 className="text-lg font-semibold text-red-800 mb-2">
                                {t("profile.eventHistory.error", "Failed to load event history")}
                            </h3>
                            <p className="text-red-600">{error.message}</p>
                        </div>
                    ) : !hasData ? (
                        <div className="text-center py-12 font-poppins">
                            <div className="w-20 h-20 bg-brand-blue/10 rounded-full flex items-center justify-center mx-auto mb-4">
                                <svg className="w-10 h-10 text-brand-blue" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth={2}
                                        d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"
                                    />
                                </svg>
                            </div>
                            <h3 className="text-lg font-semibold text-brand-blue mb-2">
                                {t("profile.eventHistory.empty", "No event history found")}
                            </h3>
                            <p className="text-gray-500 max-w-sm mx-auto">
                                {t(
                                    "profile.eventHistory.emptyDescription",
                                    "You haven't registered for any events yet. Start exploring our events!"
                                )}
                            </p>
                        </div>
                    ) : (
                        <div className="overflow-hidden rounded-xl border border-gray-200 shadow-sm font-poppins">
                            <div className="overflow-x-auto">
                                <table className="w-full">
                                    <thead className="bg-gradient-to-r from-gray-50 to-gray-100 text-brand-blue">
                                        <tr>
                                            <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider">
                                                {t("profile.eventHistory.fields.refId", "Registration Code")}
                                            </th>
                                            <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider">
                                                {t("profile.eventHistory.fields.status", "Status")}
                                            </th>
                                            <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider">
                                                {t("profile.eventHistory.fields.date", "Registration Date")}
                                            </th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-gray-100">
                                        {historyData.map((item: EventHistory, index: number) => (
                                            <tr
                                                key={item.uuehId}
                                                className="hover:bg-brand-blue/5 transition-colors"
                                                style={{ animationDelay: `${index * 50}ms` }}
                                            >
                                                <td className="px-4 py-4">
                                                    <div className="flex items-center gap-2">
                                                        <div className="w-8 h-8 rounded-lg bg-brand-blue/10 flex items-center justify-center">
                                                            <svg
                                                                className="w-4 h-4 text-brand-blue"
                                                                fill="none"
                                                                stroke="currentColor"
                                                                viewBox="0 0 24 24"
                                                            >
                                                                <path
                                                                    strokeLinecap="round"
                                                                    strokeLinejoin="round"
                                                                    strokeWidth={2}
                                                                    d="M7 20l4-16m2 16l4-16M6 9h14M4 15h14"
                                                                />
                                                            </svg>
                                                        </div>
                                                        <a href={`/registration/form?invoice=true&invoiceId=${item.uuehRefId}`}>
                                                            <span className="font-mono font-medium text-brand-blue">
                                                                {item.uuehRefId}
                                                            </span>
                                                        </a>
                                                    </div>
                                                </td>
                                                <td className="px-4 py-4">
                                                    <StatusBadge
                                                        status={
                                                            item.uuehTrnsStatusLabel === "paid"
                                                                ? t("profile.eventHistory.status.paid", "Paid")
                                                                : item.uuehTrnsStatusLabel === "pending"
                                                                    ? t("profile.eventHistory.status.pending", "Pending")
                                                                    : t("profile.eventHistory.status.expired", "Expired")
                                                        }
                                                    />
                                                </td>
                                                <td className="px-4 py-4">
                                                    <div className="flex items-center gap-2 text-gray-600">
                                                        <svg
                                                            className="w-4 h-4 text-gray-400"
                                                            fill="none"
                                                            stroke="currentColor"
                                                            viewBox="0 0 24 24"
                                                        >
                                                            <path
                                                                strokeLinecap="round"
                                                                strokeLinejoin="round"
                                                                strokeWidth={2}
                                                                d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                                                            />
                                                        </svg>
                                                        <span className="text-sm">
                                                            {formatDate(item.uuehCreatedTime)}
                                                        </span>
                                                    </div>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>

                            {/* Footer summary */}
                            <div className="px-4 py-3 bg-gray-50 border-t border-gray-200 flex items-center justify-between text-sm">
                                <span className="text-gray-600">
                                    {t("profile.eventHistory.total", "Total Events")}: {" "}
                                    <span className="font-semibold text-brand-blue">{historyData.length}</span>
                                </span>
                                <div className="flex items-center gap-4">
                                    <span className="flex items-center gap-1">
                                        <span className="w-2 h-2 rounded-full bg-green-500"></span>
                                        <span className="text-gray-600">
                                            {t("profile.eventHistory.paid", "Paid")}: {" "}
                                            {historyData.filter((h) => h.uuehTrnsStatusLabel === "paid").length}
                                        </span>
                                    </span>
                                    <span className="flex items-center gap-1">
                                        <span className="w-2 h-2 rounded-full bg-yellow-500"></span>
                                        <span className="text-gray-600">
                                            {t("profile.eventHistory.pending", "Pending")}: {" "}
                                            {historyData.filter((h) => h.uuehTrnsStatusLabel === "pending").length}
                                        </span>
                                    </span>
                                    <span className="flex items-center gap-1">
                                        <span className="w-2 h-2 rounded-full bg-red-500"></span>
                                        <span className="text-gray-600">
                                            {t("profile.eventHistory.expired", "Expired")}: {" "}
                                            {historyData.filter((h) => h.uuehTrnsStatusLabel === "expired").length}
                                        </span>
                                    </span>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default EventHistoryModal;
